import { useState } from "react";
import { Pin, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { tournaments } from "../../lib/tournamentApi";
import { Pill, fmtSlot, inputStyle, pillBtn } from "./shared";

const EXPIRY = [["", "No expiry"], ["60", "1 hour"], ["180", "3 hours"], ["720", "12 hours"]];

export default function NewsTab({ data, onChanged }) {
  const t = data.tournament;
  const [body, setBody] = useState("");
  const [level, setLevel] = useState("info");
  const [pinned, setPinned] = useState(false);
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const now = Date.now();

  async function run(fn, ok) {
    try { await fn(); if (ok) toast.success(ok); await onChanged(); }
    catch (e) { toast.error(e.message || "Couldn't save."); }
  }
  async function post() {
    if (!body.trim()) return;
    setBusy(true);
    await run(() => tournaments.createAnnouncement(t.id, {
      body: body.trim(), level, pinned, expires_in_min: expires ? Number(expires) : null,
    }), level === "urgent" ? "Posted — followers alerted." : "Posted.");
    setBody(""); setPinned(false); setLevel("info"); setExpires("");
    setBusy(false);
  }

  return (
    <div data-testid="news-tab">
      <SectionTitle label="Post an announcement" />
      <textarea value={body} onChange={(e) => setBody(e.target.value.slice(0, 280))} rows={3}
                placeholder="Lightning delay — all games paused 30 min." data-testid="announcement-body"
                style={{ ...inputStyle, resize: "vertical" }} />
      <div style={{ fontSize: 11, color: "var(--chalk-muted)", textAlign: "right" }}>{body.length}/280</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 4 }}>
        <button data-testid="announcement-level-info" onClick={() => setLevel("info")} style={pillBtn(level === "info")}>Info</button>
        <button data-testid="announcement-level-urgent" onClick={() => setLevel("urgent")} style={pillBtn(level === "urgent", "var(--chalk-red)")}>Urgent</button>
        <label style={{ fontSize: 12, display: "inline-flex", gap: 4, alignItems: "center", marginLeft: 6 }}>
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="announcement-pinned" /> Pin
        </label>
        <select value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="announcement-expiry"
                style={{ ...inputStyle, width: "auto", padding: "6px 8px", fontSize: 12 }}>
          {EXPIRY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div style={{ fontSize: 11, color: "var(--chalk-muted)", margin: "6px 0" }}>
        {level === "urgent" ? "Urgent sends a push alert to everyone following this tournament." : "Info shows on the public banner only — no push."}
      </div>
      <ChalkActionButton variant={level === "urgent" ? "danger" : "primary"} label="Post" loading={busy}
                         testId="announcement-post" onClick={post} />

      <SectionTitle label={`Announcements (${data.announcements.length})`} className="mt-4" />
      {data.announcements.map((a) => {
        const expired = a.expires_at && new Date(a.expires_at).getTime() <= now;
        return (
          <ChalkCard key={a.id} data-testid={`announcement-${a.id}`} style={{ marginBottom: 6, opacity: expired ? 0.5 : 1,
                                                                              borderLeft: a.level === "urgent" ? "3px solid var(--chalk-red)" : undefined }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
              <Pill tone={a.level === "urgent" ? "red" : "muted"}>{a.level}</Pill>
              {a.pinned && <Pill tone="green">pinned</Pill>}
              {expired && <Pill>expired</Pill>}
              <span style={{ fontSize: 11, color: "var(--chalk-muted)", marginLeft: "auto" }}>{fmtSlot(a.created_at, t.timezone)}</span>
            </div>
            <div style={{ fontSize: 14 }}>{a.body}</div>
            <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
              <button data-testid={`announcement-pin-${a.id}`} onClick={() => run(() => tournaments.patchAnnouncement(t.id, a.id, { pinned: !a.pinned }))}
                      style={_link}><Pin size={12} aria-hidden="true" /> {a.pinned ? "Unpin" : "Pin"}</button>
              {!expired && (
                <button data-testid={`announcement-expire-${a.id}`} onClick={() => run(() => tournaments.patchAnnouncement(t.id, a.id, { expire_now: true }), "Taken down.")}
                        style={_link}>Take down</button>
              )}
              <button data-testid={`announcement-delete-${a.id}`} onClick={() => run(() => tournaments.deleteAnnouncement(t.id, a.id), "Deleted.")}
                      style={{ ..._link, color: "var(--chalk-red)" }}><Trash2 size={12} aria-hidden="true" /> Delete</button>
            </div>
          </ChalkCard>
        );
      })}
    </div>
  );
}

const _link = { background: "transparent", border: "none", cursor: "pointer", fontSize: 12, color: "var(--green-mid)",
                display: "inline-flex", gap: 4, alignItems: "center", padding: 0 };
